/**
 * @fileoverview Sessions taught over the dashboard's range, split into class
 * sessions and solo lessons — the Dashboard's one chart.
 *
 * The server picks the bucket for the range (`series.unit`): weeks for a
 * range of a few months or less, months up to three years, years beyond. A
 * thirty-day range drawn as months would be one and a half columns, and ten
 * years drawn as months would be a hundred and twenty slivers.
 *
 * Hand-rolled columns rather than a chart library: twelve stacked values on
 * one axis is not a charting problem, and Recharts is not loaded in the org
 * portal for anything else. The anatomy follows the dataviz rules the rest of
 * the portal's figures use: columns capped at 24px with a 4px rounded cap and a
 * square base, a 2px surface gap between stacked segments, hairline solid
 * gridlines on round ticks, text in text tokens (never the series colour), a
 * legend for the two series, and a tooltip on hover AND keyboard focus.
 *
 * The tooltip enhances, it never gates: the Table view prints every value, so
 * nothing on the chart is readable only by pointing at it. The series colours
 * are the validated `--chart-series-*` pair (see index.css).
 */

import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Card, TextTabs } from "@/components/studio/primitives";
import { UNIT_WORDS, bucketLabel, formatCount, niceScale, seriesSummary } from "@/lib/orgReporting";

/** Plot height in px, excluding the month labels under it. */
const PLOT = 200;

export const SERIES = [
  { id: "class_sessions", label: "Class sessions", color: "var(--chart-series-1)" },
  { id: "solo", label: "Solo lessons", color: "var(--chart-series-2)" },
];

function Swatch({ color }) {
  return <span aria-hidden className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: color }} />;
}

export function Legend({ className }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1", className)} data-testid="volume-legend">
      {SERIES.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-1.5 font-sans text-[13px] text-[var(--text-secondary)]">
          <Swatch color={s.color} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

function Figure({ label, value, sub, testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="font-sans text-[12px] font-medium text-[var(--text-tertiary)]">{label}</div>
      <div className="mt-1 font-mono text-[22px] font-light leading-none text-[var(--text-primary)]">{value}</div>
      {/* A line of text truncates; a change (the Total's) wraps, so its
          direction and what it is measured against are never cut off. */}
      {sub && (
        <div className={cn("mt-1 font-sans text-[12px] text-[var(--text-tertiary)]", typeof sub === "string" && "truncate")}>
          {sub}
        </div>
      )}
    </div>
  );
}

/**
 * One bucket's values, shown while its column is pointed at or focused. It
 * opens BESIDE the column — to the right in the first half of the chart, to
 * the left in the second — so the column being read is never the one covered.
 */
function Tooltip({ bucket, unit, index, count }) {
  const toRight = index < count / 2;
  const style = toRight
    ? { left: `calc(${((index + 1) / count) * 100}% + 6px)` }
    : { right: `calc(${((count - index) / count) * 100}% + 6px)` };
  return (
    <div
      role="status"
      data-testid="volume-tooltip"
      className="pointer-events-none absolute top-0 z-10 w-[188px] rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-glass)] px-3 py-2.5 shadow-[var(--studio-shadow-soft)] backdrop-blur-xl"
      style={style}
    >
      <div className="font-sans text-[13px] font-semibold text-[var(--text-primary)]">
        {bucketLabel(bucket, unit, { year: true, long: true })}
        {bucket.partial && <span className="font-normal text-[var(--text-tertiary)]"> · so far</span>}
      </div>
      <dl className="mt-1.5 space-y-1">
        {SERIES.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-3 font-sans text-[12px]">
            <dt className="inline-flex items-center gap-1.5 text-[var(--text-secondary)]">
              <Swatch color={s.color} />
              {s.label}
            </dt>
            <dd className="font-mono tabular-nums text-[var(--text-primary)]">{formatCount(bucket[s.id])}</dd>
          </div>
        ))}
        <div className="flex items-center justify-between gap-3 border-t border-[var(--studio-divider)] pt-1 font-sans text-[12px]">
          <dt className="text-[var(--text-secondary)]">Total</dt>
          <dd className="font-mono tabular-nums font-medium text-[var(--text-primary)]">{formatCount(bucket.lessons)}</dd>
        </div>
      </dl>
    </div>
  );
}

function Columns({ buckets, unit, scale, active, setActive }) {
  const count = buckets.length;
  const last = count - 1;
  // Every label when they fit, otherwise every nth (always keeping the latest),
  // so a long range never prints labels on top of each other.
  const every = Math.max(1, Math.ceil(count / 13));
  return (
    <div className="flex">
      {/* Y axis: round ticks in muted ink, right-aligned to the gridlines. */}
      <div className="relative mr-2 w-9 shrink-0" style={{ height: PLOT }} aria-hidden>
        {scale.ticks.map((t) => (
          <span
            key={t}
            className="absolute right-0 translate-y-1/2 font-mono text-[11px] tabular-nums text-[var(--text-tertiary)]"
            style={{ bottom: `${(t / scale.max) * 100}%` }}
          >
            {formatCount(t)}
          </span>
        ))}
      </div>

      <div className="min-w-0 flex-1">
        <div className="relative" style={{ height: PLOT }} onMouseLeave={() => setActive(null)}>
          {scale.ticks.map((t) => (
            <div
              key={t}
              aria-hidden
              className={cn(
                "absolute inset-x-0 h-px",
                t === 0 ? "bg-[var(--studio-divider-strong)]" : "bg-[var(--studio-divider)]",
              )}
              style={{ bottom: `${(t / scale.max) * 100}%` }}
            />
          ))}

          <div className="absolute inset-0 flex" data-testid="analytics-chart">
            {buckets.map((m, i) => {
              const segments = SERIES.map((s) => ({ ...s, value: m[s.id] || 0 })).filter((s) => s.value > 0);
              const label = `${bucketLabel(m, unit, { year: true, long: true })}${m.partial ? " so far" : ""}: ${formatCount(
                m.lessons,
              )} sessions, ${formatCount(m.class_sessions || 0)} class sessions and ${formatCount(m.solo || 0)} solo lessons`;
              return (
                <button
                  key={m.key}
                  type="button"
                  aria-label={label}
                  data-testid={`volume-bucket-${m.key}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                  className={cn(
                    "relative flex h-full flex-1 flex-col items-center justify-end rounded-t-md outline-none transition-colors duration-150",
                    "focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
                    active === i && "bg-[var(--studio-overlay)]",
                  )}
                >
                  {/* Solo on top of class, with the 2px surface gap between
                      them; only the topmost segment takes the rounded cap. */}
                  <span className="flex w-[62%] max-w-[24px] flex-col-reverse gap-[2px]">
                    {segments.map((s, j) => (
                      <span
                        key={s.id}
                        className={cn("block w-full", j === segments.length - 1 && "rounded-t-[4px]")}
                        style={{
                          height: Math.max(2, Math.round((s.value / scale.max) * PLOT)),
                          background: s.color,
                          opacity: m.partial ? 0.72 : 1,
                        }}
                      />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>

          {active !== null && buckets[active] && (
            <Tooltip bucket={buckets[active]} unit={unit} index={active} count={count} />
          )}
        </div>

        {/* Bucket labels. On a phone every other shown one is dropped too
            (always keeping the latest), so labels never collide in 280px. */}
        <div className="mt-2 flex h-7" aria-hidden>
          {buckets.map((m, i) => {
            const shown = (last - i) % every === 0;
            const year = String(m.start || m.key).slice(0, 4);
            const prevYear = i > 0 ? String(buckets[i - 1].start || buckets[i - 1].key).slice(0, 4) : null;
            const withYear = unit !== "year" && (i === 0 || year !== prevYear);
            // Equal cells, the label centred over its column and free to
            // overhang it: a label's own width must never widen the axis.
            return (
              <div
                key={m.key}
                className={cn("relative min-w-0 flex-1", !shown ? "invisible" : ((last - i) / every) % 2 === 1 && "max-sm:invisible")}
              >
                <span className="absolute left-1/2 top-0 -translate-x-1/2 whitespace-nowrap text-center font-mono text-[11px] leading-tight text-[var(--text-tertiary)]">
                  {bucketLabel(m, unit)}
                  {withYear && <span className="block text-[10px]">{year}</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function VolumeTable({ buckets, unit }) {
  return (
    <div className="-mx-6 overflow-x-auto" data-testid="volume-table">
      <table className="w-full border-collapse font-sans text-[13px]">
        <thead>
          <tr className="border-b border-[var(--studio-divider)] text-[12px] text-[var(--text-tertiary)]">
            <th scope="col" className="px-6 py-2 text-left font-semibold">{unit === "week" ? "Week of" : unit === "year" ? "Year" : "Month"}</th>
            <th scope="col" className="px-3 py-2 text-right font-semibold">Class sessions</th>
            <th scope="col" className="px-3 py-2 text-right font-semibold">Solo lessons</th>
            <th scope="col" className="px-3 py-2 text-right font-semibold">Total</th>
            <th scope="col" className="px-6 py-2 text-right font-semibold">Sent to families</th>
          </tr>
        </thead>
        <tbody>
          {[...buckets].reverse().map((m) => (
            <tr key={m.key} className="border-b border-[var(--studio-divider)] last:border-b-0">
              <th scope="row" className="px-6 py-2 text-left font-medium text-[var(--text-primary)]">
                {bucketLabel(m, unit, { year: true })}
                {m.partial && <span className="font-normal text-[var(--text-tertiary)]"> · so far</span>}
              </th>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--text-secondary)]">{formatCount(m.class_sessions || 0)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--text-secondary)]">{formatCount(m.solo || 0)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--text-primary)]">{formatCount(m.lessons)}</td>
              <td className="px-6 py-2 text-right font-mono tabular-nums text-[var(--text-secondary)]">{formatCount(m.emails || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * @param {{
 *   buckets: Array<{key: string, start?: string, lessons: number, class_sessions?: number, solo?: number, emails?: number, partial?: boolean}>,
 *   unit?: "week"|"month"|"year",
 *   rangeLabel?: string,
 *   totalSub?: React.ReactNode,
 * }} props `totalSub` replaces the range under the Total, for the change
 *   against the range before it.
 */
export default function TeachingVolumeChart({ buckets = [], unit = "month", rangeLabel, totalSub }) {
  const [view, setView] = useState("chart");
  const [active, setActive] = useState(null);
  const scale = useMemo(() => niceScale(Math.max(0, ...buckets.map((m) => m.lessons || 0))), [buckets]);
  const summary = useMemo(() => seriesSummary(buckets), [buckets]);
  const current = summary.current;
  const words = UNIT_WORDS[unit] || UNIT_WORDS.month;

  return (
    <Card padding={24} data-testid="analytics-volume">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Sessions taught</h2>
          <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="volume-sub">
            By {words.one}{rangeLabel ? `, ${rangeLabel.toLowerCase()}` : ""}
          </p>
        </div>
        {/* Chart or table: the same numbers, the table for reading exact values. */}
        <TextTabs
          testId="volume-view"
          value={view}
          onChange={setView}
          options={[
            { id: "chart", label: "Chart", testId: "volume-view-chart" },
            { id: "table", label: "Table", testId: "volume-view-table" },
          ]}
        />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 border-b border-[var(--studio-divider)] pb-5 sm:grid-cols-4">
        <Figure label="Total" value={formatCount(summary.total)} sub={totalSub ?? rangeLabel} testId="volume-total" />
        <Figure
          label={`${words.each} average`}
          value={summary.average === null ? "—" : formatCount(summary.average)}
          sub={`complete ${words.one}s`}
          testId="volume-average"
        />
        <Figure
          label={`Busiest ${words.one}`}
          value={summary.busiest ? formatCount(summary.busiest.lessons) : "—"}
          sub={summary.busiest ? bucketLabel(summary.busiest, unit, { year: true, long: unit === "week" }) : "No sessions yet"}
          testId="volume-busiest"
        />
        {/* The bucket still running when the range ends today; otherwise the
            summaries that reached families across the range. */}
        {current ? (
          <Figure
            label={`This ${words.one} so far`}
            value={formatCount(current.lessons)}
            sub={bucketLabel(current, unit, { year: true, long: true })}
            testId="volume-current"
          />
        ) : (
          <Figure label="Sent to families" value={formatCount(summary.emails)} sub={rangeLabel} testId="volume-emails" />
        )}
      </div>

      {view === "chart" ? (
        <>
          <Legend className="mb-4 mt-5" />
          {buckets.length === 0 ? (
            <p className="py-10 text-center font-sans text-[14px] text-[var(--text-tertiary)]">Nothing taught in this range.</p>
          ) : (
            <Columns buckets={buckets} unit={unit} scale={scale} active={active} setActive={setActive} />
          )}
          <p className="mt-3 font-sans text-[12px] text-[var(--text-tertiary)]">
            {current ? `The latest ${words.one} is still in progress and drawn lighter. ` : ""}
            Point at or tab to a column for its numbers.
          </p>
        </>
      ) : (
        <div className="mt-4">
          <VolumeTable buckets={buckets} unit={unit} />
        </div>
      )}
    </Card>
  );
}
